import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { externalMembershipLabel, roleLabels } from '@brimo/shared';
import { clsx } from '../ds/clsx';
import { Banner, Button, Card, Sheet } from '../ds/components';
import { useIsCompact, useIsMobile } from '../ds/hooks';
import { api, errorMessage } from '../lib/api';
import { useRealtimeConnection } from '../lib/events';
import { sessionQueryKey, useSession, type SessionInfo } from '../lib/session';
import { AuthFrame } from '../modules/auth/AuthFrame';
import { QueueBar } from '../modules/queue/QueueBar';
import { IncomingCallLayer } from './IncomingCall';
import { MobileHeader, MobileTabBar } from './MobileChrome';
import { NotificationsPanel } from './NotificationsPanel';
import { SearchPalette } from './SearchPalette';
import { ShellActionsProvider, type ShellActions } from './ShellContext';
import { ShellSkeleton } from './ShellSkeleton';
import { Topbar } from './Topbar';
import { WorkspaceSwitcher, type WorkspaceOption } from './WorkspaceSwitcher';
import { activeNavigation, moduleLabels, navKeyForPath, type NavAccess } from './navigation';
import styles from './AppShell.module.css';

/** Route `handle` read by the mobile header. */
export interface RouteHandle {
  mobileTitle?: string;
  mobileBack?: string;
  /** false hides the search and bell of the mobile head (the "Mehr" page). */
  mobileActions?: boolean;
}

function isRouteHandle(value: unknown): value is RouteHandle {
  return typeof value === 'object' && value !== null;
}

function useLogout(): () => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST', body: {} });
    } finally {
      queryClient.clear();
      await navigate('/login', { replace: true });
    }
  }, [navigate, queryClient]);
}

/** Signed in but without a workspace: pick the first membership, or explain that there is none. */
function WorkspaceGate({ session }: { session: SessionInfo }) {
  const queryClient = useQueryClient();
  const logout = useLogout();
  const first = session.workspaces.find((workspace) => workspace.active) ?? session.workspaces[0];
  const select = useMutation({
    mutationFn: (organizationId: string) =>
      api<SessionInfo>('/auth/workspace', { method: 'POST', body: { organizationId } }),
    onSuccess: (info) => {
      queryClient.setQueryData(sessionQueryKey, info);
    },
  });
  const { mutate } = select;
  useEffect(() => {
    if (first && select.isIdle) mutate(first.organizationId);
  }, [first, mutate, select.isIdle]);

  if (first && !select.isError) return <ShellSkeleton />;
  return (
    <AuthFrame>
      <Card padded>
        <h1 className={styles.gateTitle}>Kein aktiver Workspace.</h1>
        <p className={styles.gateText}>
          Ihr Konto ist keinem aktiven Workspace zugeordnet. Wenden Sie sich an Ihren Org-Admin.
        </p>
        {select.isError ? (
          <div className={styles.gateBanner}>
            <Banner tone="danger">{errorMessage(select.error)}</Banner>
          </div>
        ) : null}
        <div className={styles.gateActions}>
          {first ? (
            <Button onClick={() => mutate(first.organizationId)} loading={select.isPending}>
              Erneut versuchen
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => void logout()}>
            Abmelden
          </Button>
        </div>
      </Card>
    </AuthFrame>
  );
}

/** The signed-in frame: topbar (or mobile head + tab bar), search palette, notifications, SSE. */
export function AppShell() {
  const session = useSession();
  if (session.active === null) return <WorkspaceGate session={session} />;
  return <ShellFrame session={session} />;
}

function ShellFrame({ session }: { session: SessionInfo }) {
  const workspace = session.active;
  if (workspace === null) throw new Error('ShellFrame needs an active workspace');
  useRealtimeConnection();
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  const queryClient = useQueryClient();
  const compact = useIsCompact();
  const mobile = useIsMobile();
  const logout = useLogout();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);

  const active = navKeyForPath(location.pathname);
  const activeNav = activeNavigation(location.pathname, location.search);
  const handle = matches
    .map((match) => match.handle)
    .filter(isRouteHandle)
    .at(-1);
  const mobileTitle = handle?.mobileTitle ?? (active ? moduleLabels[active] : 'DAMOKLES');

  useEffect(() => {
    setNotificationsOpen(false);
    setSwitcherOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const switchWorkspace = useCallback(
    async (organizationId: string) => {
      const info = await api<SessionInfo>('/auth/workspace', { method: 'POST', body: { organizationId } });
      queryClient.setQueryData(sessionQueryKey, info);
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      setSwitcherOpen(false);
      await navigate('/tasks');
    },
    [navigate, queryClient],
  );

  const actions = useMemo<ShellActions>(
    () => ({
      openSearch: () => setSearchOpen(true),
      openNotifications: () => setNotificationsOpen(true),
      openWorkspaceSwitcher: () => setSwitcherOpen(true),
      logout,
      switchWorkspace,
    }),
    [logout, switchWorkspace],
  );

  const workspaceOptions: WorkspaceOption[] = session.workspaces.map((entry) => {
    const roleLabel = (roleLabels as Record<string, string>)[entry.roleKey] ?? entry.roleKey;
    return {
      organizationId: entry.organizationId,
      name: entry.organizationName,
      // D-230: an external membership reads „Setter · als extern · White Label Agency GmbH";
      // D-288: a servicing membership names the parent organization („Servicing · White Label Agency").
      subline: entry.servicingFrom
        ? `Servicing · ${entry.servicingFrom}`
        : entry.externalCompany
          ? `${roleLabel} · als ${externalMembershipLabel(entry.externalCompany)}`
          : roleLabel,
      active: entry.organizationId === workspace.organizationId,
    };
  });
  const multiWorkspace = session.workspaces.length > 1;
  const navAccess: NavAccess = {
    tools: workspace.tools,
    permissions: workspace.permissions,
    modules: workspace.modules,
  };
  const user = workspace.user;
  const userName = `${user.firstName} ${user.lastName}`.trim();

  return (
    <ShellActionsProvider value={actions}>
      <div className={clsx(styles.shell, mobile && styles.shellMobile)}>
        {mobile ? (
          <MobileHeader
            title={mobileTitle}
            back={handle?.mobileBack}
            actions={handle?.mobileActions !== false}
            unread={workspace.unreadNotifications}
            notificationsOpen={notificationsOpen}
            onOpenSearch={() => setSearchOpen(true)}
            onToggleNotifications={() => setNotificationsOpen((open) => !open)}
            bellRef={bellRef}
          />
        ) : (
          <Topbar
            active={activeNav}
            settingsActive={active === 'settings'}
            user={{
              name: userName,
              initials: user.initials,
              roleLabel: user.roleLabel,
              avatarUrl: user.avatarUrl,
            }}
            access={navAccess}
            taskCount={workspace.taskCount}
            unread={workspace.unreadNotifications}
            queuePaused={workspace.queuePaused}
            workspace={
              multiWorkspace ? { name: workspace.organizationName, options: workspaceOptions } : undefined
            }
            compact={compact}
            notificationsOpen={notificationsOpen}
            switcherOpen={switcherOpen}
            onSwitcherOpenChange={setSwitcherOpen}
            onOpenSearch={() => setSearchOpen(true)}
            onToggleNotifications={() => setNotificationsOpen((open) => !open)}
            onResumeQueue={() => navigate('/tasks')}
            onLogout={() => void logout()}
            onSwitchWorkspace={switchWorkspace}
            bellRef={bellRef}
          />
        )}

        <QueueBar />
        <main className={styles.main} id="main">
          <Outlet />
        </main>

        {mobile ? <MobileTabBar active={active} taskCount={workspace.taskCount} /> : null}

        <IncomingCallLayer />
        <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        <NotificationsPanel
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          anchorRef={bellRef}
        />

        {mobile && multiWorkspace ? (
          <Sheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} label="Workspace wechseln">
            <WorkspaceSwitcher options={workspaceOptions} onSelect={switchWorkspace} />
          </Sheet>
        ) : null}
      </div>
    </ShellActionsProvider>
  );
}
